import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const API_BASE = (process.env.REACT_APP_API_BASE || '/api').replace(/["']/g, '').replace(/\/+$/, '');

const btnStyle = { padding: '20px 40px', fontSize: 18, fontWeight: 600, color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', transition: 'transform 0.1s' };
const btnGreen = { ...btnStyle, background: '#4CAF50' };
const btnTeal = { ...btnStyle, background: '#48A9A6' };
const btnOrange = { ...btnStyle, background: '#F28B30' };
const btnRed = { ...btnStyle, background: '#dc3545' };
const inputStyle = { width: '100%', padding: 12, margin: '8px 0', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, fontSize: 15 };
const smallBtn = { padding: '6px 12px', fontSize: 13, fontWeight: 500, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 5 };

export default function AdminTutorManagement() {
  const navigate = useNavigate();
  const location = useLocation();

  // Land on the right sub-view when entered from a dashboard button
  const initialView = () => {
    const p = location.pathname;
    if (p.endsWith('/add-tutor')) return 'add';
    if (p.endsWith('/manage-tutor')) return 'manage';
    if (p.endsWith('/assign-student-to-tutor') || p.endsWith('/assign-tutor')) return 'assign';
    return 'menu';
  };

  const [view, setView] = useState(initialView());

  const [tutors, setTutors] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedTutorId, setSelectedTutorId] = useState('');
  const [tutorStudents, setTutorStudents] = useState([]);

  const [addForm, setAddForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '' });
  const [editForm, setEditForm] = useState({ id: '', first_name: '', last_name: '', email: '', phone: '', password: '' });
  const [assignForm, setAssignForm] = useState({ student_id: '', subject: 'math' });
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (view !== 'menu') {
      fetchTutors();
      if (view === 'assign') fetchStudents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

  const fetchTutors = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/tutors`, { headers: authHeaders() });
      if (res.ok) setTutors(await res.json());
    } catch (err) { console.error(err); }
  };

  const fetchStudents = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/students`, { headers: authHeaders() });
      if (res.ok) setStudents(await res.json());
    } catch (err) { console.error(err); }
  };

  const fetchTutorStudents = async (tutorId) => {
    setSelectedTutorId(tutorId);
    try {
      const res = await fetch(`${API_BASE}/admin/tutors/${tutorId}/students`, { headers: authHeaders() });
      if (res.ok) setTutorStudents(await res.json());
    } catch (err) { console.error(err); }
  };

  const handleAddTutor = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/admin/tutors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(addForm)
      });
      const data = await res.json();
      if (res.ok) {
        setMessage('✅ Tutor created successfully!');
        setAddForm({ first_name: '', last_name: '', email: '', phone: '', password: '' });
        fetchTutors();
      } else {
        setMessage(`❌ Error: ${data.error}`);
      }
    } catch (err) { setMessage('❌ Failed to create tutor.'); }
  };

  const handleEditClick = (tutor) => {
    setEditForm({
      id: tutor.id,
      first_name: tutor.first_name,
      last_name: tutor.last_name,
      email: tutor.email,
      phone: tutor.phone || '',
      password: ''
    });
    setView('edit');
    setMessage('');
  };

  const handleUpdateTutor = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/admin/tutors/${editForm.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(editForm)
      });
      const data = await res.json();
      if (res.ok) {
        setMessage('✅ Tutor updated successfully!');
        fetchTutors();
        setTimeout(() => setView('manage'), 1200);
      } else {
        setMessage(`❌ Error: ${data.error}`);
      }
    } catch (err) { setMessage('❌ Failed to update tutor.'); }
  };

  const handleToggleStatus = async (tutorId, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      const res = await fetch(`${API_BASE}/admin/tutors/${tutorId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ is_active: newStatus })
      });
      const data = await res.json();
      if (res.ok) { setMessage(`✅ ${data.message}`); fetchTutors(); }
      else { setMessage(`❌ Error: ${data.error}`); }
    } catch (err) { setMessage('❌ Failed to update status.'); }
  };

  const handleAssign = async () => {
    if (!selectedTutorId || !assignForm.student_id) return;
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/admin/tutors/${selectedTutorId}/assign-student`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(assignForm)
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(`✅ ${data.message}`);
        setAssignForm({ student_id: '', subject: 'math' });
        fetchTutorStudents(selectedTutorId);
        fetchTutors();
      } else {
        setMessage(`❌ Error: ${data.error}`);
      }
    } catch (err) { setMessage('❌ Failed to assign.'); }
  };

  // Per-subject remove (does NOT touch the tutor's other subjects for this student)
  const handleRemoveSubject = async (studentId, subject) => {
    if (!window.confirm(`Remove this tutor from the student for ${subject.toUpperCase()} only?`)) return;
    try {
      const res = await fetch(
        `${API_BASE}/admin/tutors/${selectedTutorId}/students/${studentId}/subjects/${subject}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMessage(`✅ ${data.message || 'Subject assignment removed.'}`);
        fetchTutorStudents(selectedTutorId);
        fetchTutors();
      } else {
        setMessage(`❌ Error: ${data.error || 'Failed to remove.'}`);
      }
    } catch (err) { setMessage('❌ Failed to remove.'); }
  };

  const renderMenu = () => (
    <div style={{ textAlign: 'center', marginTop: 40 }}>
      <h2 style={{ marginBottom: 30 }}>Tutor Management</h2>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 20, flexWrap: 'wrap' }}>
        <button onClick={() => setView('add')} style={btnGreen}>+ Add Tutor</button>
        <button onClick={() => setView('manage')} style={btnTeal}>Manage Tutors</button>
        <button onClick={() => setView('assign')} style={btnOrange}>Assign Student to Tutor</button>
      </div>
      <button onClick={() => navigate('/admin/dashboard')} style={{ ...btnTeal, marginTop: 40, fontSize: 14, padding: '10px 20px' }}>← Back to Dashboard</button>
    </div>
  );

  const renderAdd = () => (
    <div style={{ maxWidth: 500, margin: '40px auto', background: '#fff', padding: 30, borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      <h2>+ Add New Tutor</h2>
      <form onSubmit={handleAddTutor}>
        <input style={inputStyle} placeholder="First Name" value={addForm.first_name} onChange={e => setAddForm({ ...addForm, first_name: e.target.value })} required />
        <input style={inputStyle} placeholder="Last Name" value={addForm.last_name} onChange={e => setAddForm({ ...addForm, last_name: e.target.value })} required />
        <input style={inputStyle} placeholder="Email" type="email" value={addForm.email} onChange={e => setAddForm({ ...addForm, email: e.target.value })} required />
        <input style={inputStyle} placeholder="Phone (Optional)" value={addForm.phone} onChange={e => setAddForm({ ...addForm, phone: e.target.value })} />
        <input style={inputStyle} placeholder="Password (Min 6 chars)" type="password" value={addForm.password} onChange={e => setAddForm({ ...addForm, password: e.target.value })} required />
        <button type="submit" style={{ ...btnGreen, width: '100%', marginTop: 10 }}>Create Tutor</button>
      </form>
      {message && <p style={{ textAlign: 'center', marginTop: 15, fontWeight: 600, color: message.includes('✅') ? 'green' : 'red' }}>{message}</p>}
      <button onClick={() => setView('menu')} style={{ ...btnTeal, marginTop: 20, fontSize: 14, width: '100%' }}>← Back to Menu</button>
    </div>
  );

  const renderEdit = () => (
    <div style={{ maxWidth: 500, margin: '40px auto', background: '#fff', padding: 30, borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      <h2>✏️ Edit Tutor Information</h2>
      <form onSubmit={handleUpdateTutor}>
        <input style={inputStyle} placeholder="First Name" value={editForm.first_name} onChange={e => setEditForm({ ...editForm, first_name: e.target.value })} required />
        <input style={inputStyle} placeholder="Last Name" value={editForm.last_name} onChange={e => setEditForm({ ...editForm, last_name: e.target.value })} required />
        <input style={inputStyle} placeholder="Email" type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} required />
        <input style={inputStyle} placeholder="Phone (Optional)" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} />
        <input style={inputStyle} placeholder="New Password (Leave blank to keep current)" type="password" value={editForm.password} onChange={e => setEditForm({ ...editForm, password: e.target.value })} />
        <button type="submit" style={{ ...btnOrange, width: '100%', marginTop: 10 }}>Update Tutor</button>
      </form>
      {message && <p style={{ textAlign: 'center', marginTop: 15, fontWeight: 600, color: message.includes('✅') ? 'green' : 'red' }}>{message}</p>}
      <button onClick={() => setView('manage')} style={{ ...btnTeal, marginTop: 20, fontSize: 14, width: '100%' }}>← Back to Manage</button>
    </div>
  );

  const renderManage = () => (
    <div style={{ maxWidth: 950, margin: '40px auto' }}>
      <h2>Manage Tutors</h2>
      {message && <p style={{ textAlign: 'center', fontWeight: 600, color: message.includes('✅') ? 'green' : 'red' }}>{message}</p>}

      {tutors.length === 0 ? <p style={{ textAlign: 'center', color: '#888' }}>No tutors found.</p> : (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #ddd', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #eee', textAlign: 'left' }}>
                <th style={{ padding: 15 }}>Name</th>
                <th style={{ padding: 15 }}>Email</th>
                <th style={{ padding: 15 }}>Phone</th>
                <th style={{ padding: 15, textAlign: 'center' }}>Status</th>
                <th style={{ padding: 15, textAlign: 'center' }}>Students</th>
                <th style={{ padding: 15, textAlign: 'center' }}>Subjects</th>
                <th style={{ padding: 15, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tutors.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 15, fontWeight: 600 }}>{t.first_name} {t.last_name}</td>
                  <td style={{ padding: 15, color: '#555' }}>{t.email}</td>
                  <td style={{ padding: 15, color: '#555' }}>{t.phone || '—'}</td>
                  <td style={{ padding: 15, textAlign: 'center' }}>
                    <span style={{ padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: t.is_active ? '#d4edda' : '#f8d7da', color: t.is_active ? '#155724' : '#721c24' }}>
                      {t.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: 15, textAlign: 'center', fontWeight: 600 }}>{t.distinct_student_count ?? t.student_count ?? 0}</td>
                  <td style={{ padding: 15, textAlign: 'center', color: '#555' }}>{t.assignment_count ?? 0}</td>
                  <td style={{ padding: 15, textAlign: 'center' }}>
                    <button onClick={() => handleEditClick(t)} style={{ ...smallBtn, background: '#17a2b8' }}>✏️ Edit</button>
                    <button onClick={() => handleToggleStatus(t.id, t.is_active)} style={{ ...smallBtn, background: t.is_active ? '#dc3545' : '#28a745' }}>
                      {t.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button onClick={() => setView('menu')} style={{ ...btnTeal, marginTop: 20, fontSize: 14, width: '100%', maxWidth: 200 }}>← Back to Menu</button>
    </div>
  );

  const renderAssign = () => (
    <div style={{ maxWidth: 850, margin: '40px auto' }}>
      <h2>Assign Student to Tutor</h2>
      {message && <p style={{ textAlign: 'center', fontWeight: 600, color: message.includes('✅') ? 'green' : 'red' }}>{message}</p>}

      <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
        <label style={{ fontWeight: 600 }}>1. Select Tutor:</label>
        <select style={inputStyle} value={selectedTutorId} onChange={e => { setSelectedTutorId(e.target.value); fetchTutorStudents(e.target.value); setMessage(''); }}>
          <option value="">-- Choose a Tutor --</option>
          {tutors.filter(t => t.is_active).map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
        </select>

        {selectedTutorId && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 10, marginTop: 15, alignItems: 'end' }}>
              <div>
                <label style={{ fontWeight: 600 }}>2. Select Student:</label>
                <select style={inputStyle} value={assignForm.student_id} onChange={e => setAssignForm({ ...assignForm, student_id: e.target.value })}>
                  <option value="">-- Choose a Student --</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name} (Grade {s.current_grade || '?'})</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 600 }}>3. Subject:</label>
                <select style={inputStyle} value={assignForm.subject} onChange={e => setAssignForm({ ...assignForm, subject: e.target.value })}>
                  <option value="math">Math</option>
                  <option value="english">English</option>
                  <option value="science">Science</option>
                </select>
              </div>
              <button onClick={handleAssign} style={{ ...btnGreen, padding: '12px 20px', fontSize: 16 }}>Assign</button>
            </div>
            <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
              If this student already has a different tutor for the chosen subject, that tutor will be replaced automatically.
            </p>
          </>
        )}
      </div>

      {selectedTutorId && (
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
          <h3>Current Assignments for this Tutor</h3>
          {tutorStudents.length === 0 ? <p style={{ color: '#888' }}>No students assigned yet.</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                  <th style={{ padding: 10 }}>Student Name</th>
                  <th style={{ padding: 10 }}>Grade</th>
                  <th style={{ padding: 10 }}>Subject</th>
                  <th style={{ padding: 10 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {tutorStudents.map((ts) => (
                  <tr key={`${ts.student_id}-${ts.subject}`} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 10 }}>{ts.first_name} {ts.last_name}</td>
                    <td style={{ padding: 10 }}>{ts.current_grade || '—'}</td>
                    <td style={{ padding: 10 }}>
                      <span style={{ padding: '4px 8px', background: '#e7f1ff', color: '#0056b3', borderRadius: 4, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{ts.subject}</span>
                    </td>
                    <td style={{ padding: 10 }}>
                      <button onClick={() => handleRemoveSubject(ts.student_id, ts.subject)} style={{ ...smallBtn, background: '#dc3545' }}>Remove {ts.subject}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <button onClick={() => setView('menu')} style={{ ...btnTeal, marginTop: 20, fontSize: 14, width: '100%', maxWidth: 200 }}>← Back to Menu</button>
    </div>
  );

  return (
    <div style={{ padding: 20, minHeight: '100vh', background: '#f8f9fa' }}>
      {view === 'menu' && renderMenu()}
      {view === 'add' && renderAdd()}
      {view === 'manage' && renderManage()}
      {view === 'edit' && renderEdit()}
      {view === 'assign' && renderAssign()}
    </div>
  );
}